import { useEffect, useState, useCallback, useRef } from 'react'
import { io } from 'socket.io-client'
import { API_BASE_URL, SOCKET_URL } from '../services/apiConfig'

export function useSocket() {
  const [socket, setSocket] = useState(null)
  const [isConnected, setIsConnected] = useState(false)
  const [backendStatus, setBackendStatus] = useState('checking') // 'connected' | 'disconnected' | 'error'
  const healthIntervalRef = useRef(null)

  // Health check - actually verify backend is working
  const checkHealth = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/health`, {
        method: 'GET',
        signal: AbortSignal.timeout(5000)
      })
      if (response.ok) {
        const data = await response.json()
        if (data.success && data.database === 'connected') {
          setBackendStatus('connected')
        } else {
          setBackendStatus('error')
        }
      } else {
        setBackendStatus('error')
      }
    } catch {
      setBackendStatus('disconnected')
    }
  }, [])

  // Run health check on mount and every 30 seconds
  useEffect(() => {
    checkHealth()
    healthIntervalRef.current = setInterval(checkHealth, 30000)
    return () => {
      if (healthIntervalRef.current) clearInterval(healthIntervalRef.current)
    }
  }, [checkHealth])

  // Socket connection
  useEffect(() => {
    if (!SOCKET_URL) {
      setSocket(null)
      setIsConnected(false)
      return undefined
    }

    const socketInstance = io(SOCKET_URL, {
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    })

    socketInstance.on('connect', () => {
      setIsConnected(true)
    })

    socketInstance.on('disconnect', () => {
      setIsConnected(false)
    })

    socketInstance.on('connect_error', () => {
      setIsConnected(false)
    })

    setSocket(socketInstance)

    return () => {
      socketInstance.disconnect()
    }
  }, [])

  const joinEmployeeRoom = (employeeId) => {
    if (socket) {
      socket.emit('join-employee-room', employeeId)
    }
  }

  const joinSecurityRoom = () => {
    if (socket) {
      socket.emit('join-security-room')
    }
  }

  const joinAdminRoom = () => {
    if (socket) {
      socket.emit('join-admin-room')
    }
  }

  return {
    socket,
    isConnected,
    backendStatus,
    checkHealth,
    joinEmployeeRoom,
    joinSecurityRoom,
    joinAdminRoom,
  }
}
